import { getAddressMetadata } from "@/lib/utils";
import { FIXED_DELIVERY_HOURS } from "@/lib/fixed-delivery-schedule";
import ConfirmDelete from "@/components/molecules/confirm-delete";
import { Locales } from "@/lib/i18n";
import { sdk } from "@/lib/medusa/client";
import { useCustomer } from "@/hooks/useCustomer";
import { useState, useTransition } from "react";
import { toast } from "@/components/atoms/toast";
import { __ } from "@/lib/i18n/functions";
import ModalTemplate from "@/components/molecules/modal-template";
import ShippingAddressForm from "@/components/organisms/forms/checkout/shipping-address-form";
import { DeliveryAndBillingSchema } from "@/lib/validation/payment";
import { useCheckoutStore } from "@/stores/checkout-store";
import { UseFormSetValue } from "react-hook-form";
import { getQueryClient } from "@/lib/query";
import { companyAddressesQueryKey } from "@/queries/company";
import { DeliveryAddress } from "@/types";

type ShippingAddressCardProps = {
  address: DeliveryAddress;
  locale: Locales;
  type: "checkout" | "account";
  setValue?: UseFormSetValue<DeliveryAndBillingSchema>;
  addressSource?: "customer" | "company";
  companyId?: string | null;
  canManage?: boolean;
};

const getDeliveryLocationLabel = (value: string | undefined, locale: Locales) => {
  const labels: Record<string, string> = {
    house: "House",
    apartment: "Apartment",
    office: "Office / company",
    venue: "Event venue",
    warehouse: "Warehouse",
    school: "School",
  };

  return value ? __(labels[value] || value, locale) : "";
};

const getParkingLabel = (value: string | undefined, locale: Locales) => {
  const labels: Record<string, string> = {
    front: "Right in front",
    private_parking: "Private parking",
    loading_zone: "Loading zone",
    complicated: "Complicated parking",
    special_case: "Special case",
  };

  return value ? __(labels[value] || value, locale) : "";
};

const ShippingAddressCard = ({
  address,
  locale,
  type,
  setValue,
  addressSource = "customer",
  companyId,
  canManage = true,
}: ShippingAddressCardProps) => {
  const metadata = getAddressMetadata(address);
  const queryClient = getQueryClient();
  const setData = useCheckoutStore((state) => state.setData);
  const [isPending, startTransition] = useTransition();
  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { refreshCustomer } = useCustomer();

  const handleDeleteAddress = (addressId: string) => {
    startTransition(async () => {
      try {
        if (addressSource === "company" && companyId) {
          await sdk.client.fetch(`store/companies/${companyId}/addresses/${addressId}`, {
            method: "DELETE",
          });
          queryClient.invalidateQueries({ queryKey: companyAddressesQueryKey(companyId) });
        } else {
          await sdk.store.customer.deleteAddress(addressId);
          await refreshCustomer();
        }
        if (type === "checkout" && setValue) {
          setValue("deliveryAddress", "");
          setData({ deliveryAddress: "" });
        }
        toast.success({ title: __("Address deleted successfully", locale) });
      } catch (error) {
        toast.error({
          title: __("Error deleting address", locale),
          description: __("Please try again later.", locale),
        });
        console.error("Error deleting address:", error);
      } finally {
        setIsOpen(false);
      }
    });
  };

  return (
    <div className="border-primary-light/30 space-y-2.5 rounded-md border p-4">
      <div className="flex flex-wrap items-center justify-between">
        {(address.address_name || metadata.address_name) && (
          <h4 className="font-cabinet-grotesk text-xl font-bold">{address.address_name || metadata.address_name}</h4>
        )}
        {canManage && (
          <div className="flex items-center gap-2.5">
            <ConfirmDelete
              open={isOpen}
              onOpenChange={setIsOpen}
              onClick={() => handleDeleteAddress(address.id)}
              isActionHappening={isPending}
              locale={locale}
              text={__("Are you sure you want to delete this address?", locale)}
              modalTrigger={<button className="cursor-pointer underline">{__("Delete", locale)}</button>}
              title={__("Delete address", locale)}
            />
            <ModalTemplate
              open={isModalOpen}
              onOpenChange={setIsModalOpen}
              classNameTitle="font-cabinet-grotesk leading-h1 text-lg md:text-1xl"
              title={__("Edit shipping address", locale)}
              triggerAsChild
              modalTrigger={<button className="cursor-pointer underline">{__("Edit", locale)}</button>}
            >
              <ShippingAddressForm
                locale={locale}
                onSuccess={() => {
                  setIsModalOpen(false);
                }}
                addressToEdit={address}
                addressId={address.id}
                addressSource={addressSource}
                companyId={companyId}
              />
            </ModalTemplate>
          </div>
        )}
      </div>
      <dl>
        <div className="h-fit space-y-2.5">
          <div className="flex flex-wrap gap-1.5">
            <dt>{__("Postal Address", locale)} : </dt>
            <dd className="font-semibold">{address.address_1}</dd>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <dt>{__("ZIP Code", locale)} :</dt>
            <dd className="font-semibold">{address.postal_code}</dd>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <dt>{__("City", locale)} : </dt>
            <dd className="font-semibold">{address.city}</dd>
          </div>
          {metadata.delivery_location_type && (
            <div className="flex flex-wrap gap-1.5">
              <dt>{__("Location type", locale)} : </dt>
              <dd className="font-semibold">{getDeliveryLocationLabel(metadata.delivery_location_type, locale)}</dd>
            </div>
          )}
          {metadata.delivery_floor && (
            <div className="flex flex-wrap gap-1.5">
              <dt>{__("Delivery floor", locale)} : </dt>
              <dd className="font-semibold">{metadata.delivery_floor}</dd>
            </div>
          )}
          {typeof metadata.delivery_has_elevator === "boolean" && (
            <div className="flex flex-wrap gap-1.5">
              <dt>{__("Elevator", locale)} : </dt>
              <dd className="font-semibold">{metadata.delivery_has_elevator ? __("Yes", locale) : __("No", locale)}</dd>
            </div>
          )}
          {metadata.delivery_parking_type && (
            <div className="flex flex-wrap gap-1.5">
              <dt>{__("Parking", locale)} : </dt>
              <dd className="font-semibold">{getParkingLabel(metadata.delivery_parking_type, locale)}</dd>
            </div>
          )}
          <div className="flex flex-wrap gap-1.5">
            <dt>{__("Delivery schedule", locale)} : </dt>
            <dd className="font-semibold">{FIXED_DELIVERY_HOURS}</dd>
          </div>
          {(metadata.delivery_contact_name || metadata.delivery_contact_phone) && (
            <div className="flex flex-wrap gap-1.5">
              <dt>{__("On-site contact", locale)} : </dt>
              <dd className="font-semibold">
                {[metadata.delivery_contact_name, metadata.delivery_contact_phone].filter(Boolean).join(" - ")}
              </dd>
            </div>
          )}
          {(metadata.delivery_instructions || metadata.message) && (
            <div className="flex flex-wrap gap-1.5">
              <dt>{__("Delivery instructions", locale)} : </dt>
              <dd className="font-semibold">{metadata.delivery_instructions || metadata.message}</dd>
            </div>
          )}
        </div>
        <div className="w-fit space-y-2.5"></div>
      </dl>
    </div>
  );
};

export default ShippingAddressCard;
